import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Check, AlertTriangle, Eye, EyeOff, X, Clock } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiTokenApi } from '../../services/api';
import './ApiKeysSettings.css';

function ApiKeysSettings() {
    const { currentWorkspace } = useWorkspace();

    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTokenName, setNewTokenName] = useState('');
    const [newTokenExpiry, setNewTokenExpiry] = useState('');
    const [creating, setCreating] = useState(false);

    // Reveal state — shown once after creation
    const [revealedToken, setRevealedToken] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    // Revoke state
    const [revoking, setRevoking] = useState(null);

    const loadTokens = async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const { data } = await apiTokenApi.list(currentWorkspace.id);
            setTokens(data.tokens || []);
        } catch (err) {
            setError('Failed to load API tokens');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTokens();
    }, [currentWorkspace?.id]);

    const handleCreate = async () => {
        if (!newTokenName.trim() || !currentWorkspace?.id) return;
        setCreating(true);
        setError(null);
        try {
            const { data } = await apiTokenApi.create({
                workspaceId: currentWorkspace.id,
                name: newTokenName.trim(),
                expiresAt: newTokenExpiry || null
            });
            // Show the raw token ONCE
            setRevealedToken({ id: data.id, token: data.token, name: data.name });
            setShowCreateModal(false);
            setNewTokenName('');
            setNewTokenExpiry('');
            await loadTokens();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create token');
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async (token) => {
        setRevoking(token.id);
        try {
            await apiTokenApi.revoke(token.id);
            setTokens(prev => prev.filter(t => t.id !== token.id));
        } catch (err) {
            setError('Failed to revoke token');
        } finally {
            setRevoking(null);
        }
    };

    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

    return (
        <div className="api-keys-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>API Keys</h1>
                    <p>Generate long-lived tokens for CI/CD pipelines and automation. Keep your tokens secure — they are shown only once.</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                    <Plus size={16} />
                    New API Key
                </button>
            </div>

            {error && (
                <div className="alert-banner error" style={{ marginBottom: '1.5rem' }}>
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* One-time Token Reveal Banner */}
            {revealedToken && (
                <div className="token-reveal-banner">
                    <div className="token-reveal-header">
                        <div>
                            <strong>✅ Token Created: {revealedToken.name}</strong>
                            <p>Copy this token now. It will <strong>not be shown again</strong>.</p>
                        </div>
                        <button className="btn-icon" onClick={() => setRevealedToken(null)}>
                            <X size={18} />
                        </button>
                    </div>
                    <div className="token-reveal-value">
                        <code>{revealedToken.token}</code>
                        <button
                            className={`btn-copy ${copiedId === revealedToken.id ? 'copied' : ''}`}
                            onClick={() => handleCopy(revealedToken.token, revealedToken.id)}
                        >
                            {copiedId === revealedToken.id ? <Check size={15} /> : <Copy size={15} />}
                            {copiedId === revealedToken.id ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                </div>
            )}

            {/* Tokens List */}
            <div className="settings-section-row">
                <div className="section-info">
                    <h2>Active Keys</h2>
                    <p>Keys are workspace-scoped; they authenticate as the user who created them.</p>
                </div>
                <div className="section-card">
                    {loading ? (
                        <div className="empty-state-sm">Loading...</div>
                    ) : tokens.length === 0 ? (
                        <div className="empty-state">
                            <Key size={32} opacity={0.4} />
                            <p>No API keys yet. Create one to use in your CI pipeline.</p>
                        </div>
                    ) : (
                        <table className="tokens-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Token</th>
                                    <th>Last Used</th>
                                    <th>Expires</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tokens.map(token => (
                                    <tr key={token.id} className={isExpired(token.expiresAt) ? 'expired-row' : ''}>
                                        <td>
                                            <div className="token-name">
                                                <Key size={14} />
                                                {token.name}
                                                {isExpired(token.expiresAt) && (
                                                    <span className="badge badge-danger">Expired</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <code className="token-prefix">{token.prefix}••••••••••••••••</code>
                                        </td>
                                        <td className="text-muted">
                                            {token.lastUsedAt
                                                ? new Date(token.lastUsedAt).toLocaleDateString()
                                                : 'Never'}
                                        </td>
                                        <td className="text-muted">
                                            {token.expiresAt
                                                ? new Date(token.expiresAt).toLocaleDateString()
                                                : <span className="text-success">Never</span>}
                                        </td>
                                        <td>
                                            <button
                                                className="btn-revoke"
                                                onClick={() => handleRevoke(token)}
                                                disabled={revoking === token.id}
                                                title="Revoke token"
                                            >
                                                {revoking === token.id ? '...' : <Trash2 size={15} />}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Usage Guide */}
            <div className="settings-section-row">
                <div className="section-info">
                    <h2>Usage</h2>
                    <p>Use your API key as a <code>Bearer</code> token in your CI workflow secrets.</p>
                </div>
                <div className="section-card usage-card">
                    <p className="usage-label">GitHub Actions example</p>
                    <pre className="code-block">{`- name: Upload to Rova AI
  uses: rova-mobile/upload-action@v1
  with:
    api-token: \${{ secrets.ROVA_API_TOKEN }}
    workspace-id: \${{ secrets.ROVA_WORKSPACE_ID }}
    parent-app-id: 'your-app-id'
    app-path: 'app.apk'
    platform: 'android'`}</pre>
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create API Key</h2>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="tokenName">Name <span className="required">*</span></label>
                                <input
                                    id="tokenName"
                                    type="text"
                                    className="form-input"
                                    placeholder='e.g. "GitHub Actions – iOS"'
                                    value={newTokenName}
                                    onChange={e => setNewTokenName(e.target.value)}
                                    autoFocus
                                />
                                <span className="input-help">A memorable label so you know which key is which.</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="tokenExpiry">Expiry Date <span className="optional">(optional)</span></label>
                                <input
                                    id="tokenExpiry"
                                    type="date"
                                    className="form-input"
                                    value={newTokenExpiry}
                                    onChange={e => setNewTokenExpiry(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                />
                                <span className="input-help">Leave blank for a non-expiring token.</span>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreate}
                                disabled={!newTokenName.trim() || creating}
                            >
                                {creating ? 'Creating...' : 'Create Token'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ApiKeysSettings;
